/* eslint-disable ember/no-classic-components */
import Component from "@ember/component";
import { fn } from "@ember/helper";
import { action } from "@ember/object";
import { service } from "@ember/service";
import { tagName } from "@ember-decorators/component";
import { removeValueFromArray } from "discourse/lib/array-tools";
import { bind } from "discourse/lib/decorators";
import { openLightbox } from "discourse/lib/lightbox";
import { cloneJSON } from "discourse/lib/object";
import { autoTrackedArray } from "discourse/lib/tracked-tools";
import UppyUpload from "discourse/lib/uppy/uppy-upload";
import UppyMediaOptimization from "discourse/lib/uppy-media-optimization-plugin";
import { clipboardHelpers } from "discourse/lib/utilities";
import DPickFilesButton from "discourse/ui-kit/d-pick-files-button";
import ChatComposerUpload from "discourse/plugins/chat/discourse/components/chat-composer-upload";

@tagName("")
export default class ChatComposerUploads extends Component {
  @service capabilities;
  @service mediaOptimizationWorker;

  @autoTrackedArray uploads = null;

  uppyUpload = new UppyUpload(this, {
    id: "chat-composer-uploader",
    type: "chat-composer",
    useMultipartUploadsIfAvailable: true,

    uppyReady: () => {
      if (this.siteSettings.composer_media_optimization_image_enabled) {
        this.uppyUpload.uppyWrapper.useUploadPlugin(UppyMediaOptimization, {
          optimizeFn: (data, opts) =>
            this.mediaOptimizationWorker.optimizeImage(data, opts),
          cancelFn: (fileId) => this.mediaOptimizationWorker.cancel(fileId),
          runParallel: !this.capabilities.isMobileDevice,
        });
      }

      this.uppyUpload.uppyWrapper.onPreProcessProgress((file) => {
        const inProgressUpload = this.inProgressUploads.find(
          (item) => item.id === file.id
        );
        if (!inProgressUpload?.processing) {
          inProgressUpload?.set("processing", true);
        }
      });

      this.uppyUpload.uppyWrapper.onPreProcessComplete((file) => {
        const inProgressUpload = this.inProgressUploads.find(
          (item) => item.id === file.id
        );
        inProgressUpload?.set("processing", false);
      });
    },

    uploadDone: (upload) => {
      this.uploads.push(upload);
      this._triggerUploadsChanged();
    },

    uploadDropTargetOptions: () => ({
      target: this.uploadDropZone || document.body,
    }),

    onProgressUploadsChanged: () => {
      this._triggerUploadsChanged(this.uploads, {
        inProgressUploadsCount: this.inProgressUploads?.length,
      });
    },
  });

  existingUploads = null;
  uploadDropZone = null;

  #previews = new WeakMap();

  get inProgressUploads() {
    return this.uppyUpload.inProgressUploads;
  }

  get showUploadsContainer() {
    return this.uploads?.length > 0 || this.inProgressUploads.length > 0;
  }

  didReceiveAttrs() {
    super.didReceiveAttrs(...arguments);
    if (this.inProgressUploads?.length > 0) {
      this.uppyUpload.cancelAllUploads();
    }

    this.uploads = this.existingUploads ? cloneJSON(this.existingUploads) : [];
  }

  didInsertElement() {
    super.didInsertElement(...arguments);

    this.composerInputEl?.addEventListener("paste", this._pasteEventListener);
  }

  willDestroyElement() {
    super.willDestroyElement(...arguments);

    this.composerInputEl?.removeEventListener(
      "paste",
      this._pasteEventListener
    );
  }

  @action
  cancelUploading(upload) {
    this.uppyUpload.cancelSingleUpload({
      fileId: upload.id,
    });
    this.removeUpload(upload);
  }

  @action
  removeUpload(upload) {
    removeValueFromArray(this.uploads, upload);
    this._triggerUploadsChanged();
  }

  @action
  openPreview(upload) {
    const shown = [...this.uploads, ...this.inProgressUploads].filter((item) =>
      this.#previews.has(item)
    );

    // own links to unfinished files, so tiles can release theirs at any time
    const localUrls = [];
    const slides = shown.map((item) => {
      const { type, element, alt } = this.#previews.get(item);
      let src = element.currentSrc || element.src;
      if (item.data instanceof Blob) {
        src = URL.createObjectURL(item.data);
        localUrls.push(src);
      }
      return { type, src, element, alt };
    });

    openLightbox(slides, { index: shown.indexOf(upload) }).finally(() =>
      localUrls.forEach((url) => URL.revokeObjectURL(url))
    );
  }

  @bind
  registerPreview(upload, preview) {
    this.#previews.set(upload, preview);
    return () => {
      if (this.#previews.get(upload) === preview) {
        this.#previews.delete(upload);
      }
    };
  }

  @bind
  _pasteEventListener(event) {
    if (document.activeElement !== this.composerInputEl) {
      return;
    }

    const { canUpload, canPasteHtml, types } = clipboardHelpers(event, {
      siteSettings: this.siteSettings,
      canUpload: true,
    });

    if (!canUpload || canPasteHtml || types.includes("text/plain")) {
      return;
    }

    if (event && event.clipboardData && event.clipboardData.files) {
      this.uppyUpload.addFiles([...event.clipboardData.files], {
        pasted: true,
      });
    }
  }

  _triggerUploadsChanged() {
    this.onUploadChanged?.(this.uploads, {
      inProgressUploadsCount: this.inProgressUploads?.length,
    });
  }

  <template>
    <div class="chat-composer-uploads" ...attributes>
      {{#if this.showUploadsContainer}}
        <div class="chat-composer-uploads-container">
          {{#each this.uploads as |upload|}}
            <ChatComposerUpload
              @isDone={{true}}
              @onCancel={{fn this.removeUpload upload}}
              @onPreview={{fn this.openPreview upload}}
              @registerPreview={{fn this.registerPreview upload}}
              @upload={{upload}}
            />
          {{/each}}

          {{#each this.inProgressUploads as |upload|}}
            <ChatComposerUpload
              @onCancel={{fn this.cancelUploading upload}}
              @onPreview={{fn this.openPreview upload}}
              @registerPreview={{fn this.registerPreview upload}}
              @upload={{upload}}
            />
          {{/each}}
        </div>
      {{/if}}

      <DPickFilesButton
        @allowMultiple={{true}}
        @fileInputClass="hidden-upload-field"
        @fileInputId={{this.fileUploadElementId}}
        @registerFileInput={{this.uppyUpload.setup}}
      />
    </div>
  </template>
}
